/**
 * ModelingColumnChart — the kit primitive for a monthly column series (e.g. the
 * cash-flow section's net cash flow per month) with an OPTIONAL cumulative line
 * overlaid and an OPTIONAL vertical reference marker (e.g. the break-even month).
 * One of only two files allowed to import recharts (kit-first rule).
 *
 * Columns are colored by sign via the ledger status tokens (positive = credit
 * green, negative = debit red) using per-bar `<Cell>`s. The cumulative overlay is
 * a thin brand-teal line on the SAME y-axis as the columns (cumulative net income
 * shares the dollar scale with monthly net income, so a single axis stays
 * honest — no misleading dual scale). Per-point tooltip shows the month, the
 * column value, and the cumulative value when present.
 *
 * SSR: wrapped in `ChartFrame` (fixed-height box + client-only mount; recharts'
 * ResponsiveContainer needs a real width). Colors resolve from design-system CSS
 * vars via `useChartColors` (dark-mode safe).
 */
import { useMemo, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';
import {
  Bar,
  CartesianGrid,
  Cell,
  ComposedChart,
  Line,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { ChartFrame, TooltipSwatch, useChartColors } from './chart-internals';
import styles from './charts.module.css';

/** One monthly point for the column chart. */
export interface ModelingColumnPoint {
  /** 1-based month index (or any integer x). */
  readonly month: number;
  /** The column value (e.g. monthly net cash flow; may be negative). */
  readonly value: number;
  /** Optional cumulative value for the overlay line (e.g. cumulative NI). */
  readonly cumulative?: number | null;
}

export interface ModelingColumnChartProps {
  readonly points: readonly ModelingColumnPoint[];
  readonly ariaLabel: string;
  /** Series label for the columns (legend + tooltip). */
  readonly valueLabel: string;
  /** Series label for the cumulative overlay; omit to hide the overlay. */
  readonly cumulativeLabel?: string | undefined;
  /** Month to mark with a vertical reference line (e.g. break-even). */
  readonly referenceMonth?: number | null | undefined;
  /** Label for the reference marker (legend); defaults to "Break-even". */
  readonly referenceLabel?: string | undefined;
  readonly xAxisLabel?: string | undefined;
  readonly yAxisLabel?: string | undefined;
  readonly valueFormatter?: ((value: number) => string) | undefined;
  /** Reserved box height in px. Default 300. */
  readonly height?: number | undefined;
  readonly className?: string | undefined;
}

interface ColumnRow {
  month: number;
  value: number;
  cumulative: number | null;
}

function defaultValueFormatter(value: number): string {
  return value.toLocaleString('en-US', { maximumFractionDigits: 0 });
}

export function ModelingColumnChart({
  points,
  ariaLabel,
  valueLabel,
  cumulativeLabel,
  referenceMonth,
  referenceLabel,
  xAxisLabel,
  yAxisLabel,
  valueFormatter = defaultValueFormatter,
  height = 300,
  className,
}: ModelingColumnChartProps): ReactNode {
  const { t } = useTranslation();
  const colors = useChartColors();
  const rows = useMemo<ColumnRow[]>(
    () =>
      points.map((p) => ({
        month: p.month,
        value: p.value,
        cumulative: p.cumulative ?? null,
      })),
    [points],
  );

  const showCumulative = cumulativeLabel !== undefined && rows.some((r) => r.cumulative !== null);
  const hasReference = referenceMonth !== null && referenceMonth !== undefined;
  const refMarkerLabel = referenceLabel ?? t('charts.breakEven');

  return (
    <div className={className}>
      <div className={styles.legend}>
        <span className={styles.legendItem}>
          <span className={styles.swatch} style={{ background: colors.positive }} aria-hidden />
          {valueLabel}
        </span>
        {showCumulative ? (
          <span className={styles.legendItem}>
            <span className={styles.swatchLine} style={{ borderTopColor: colors.cumulative }} aria-hidden />
            {cumulativeLabel}
          </span>
        ) : null}
        {hasReference ? (
          <span className={styles.legendItem}>
            <span className={styles.swatchDashed} style={{ borderTopColor: colors.reference }} aria-hidden />
            {refMarkerLabel}
          </span>
        ) : null}
      </div>

      <ChartFrame height={height} ariaLabel={ariaLabel}>
        <ResponsiveContainer width="100%" height="100%">
          <ComposedChart data={rows} margin={{ top: 8, right: 16, bottom: xAxisLabel ? 24 : 8, left: yAxisLabel ? 16 : 0 }}>
            <CartesianGrid stroke={colors.grid} vertical={false} />
            <XAxis
              dataKey="month"
              type="number"
              domain={['dataMin', 'dataMax']}
              tick={{ fill: colors.axis, fontSize: 11 }}
              tickLine={{ stroke: colors.grid }}
              axisLine={{ stroke: colors.grid }}
              {...(xAxisLabel
                ? { label: { value: xAxisLabel, position: 'insideBottom', offset: -12, fill: colors.axisLabel, fontSize: 12 } }
                : {})}
            />
            <YAxis
              tick={{ fill: colors.axis, fontSize: 11 }}
              tickLine={{ stroke: colors.grid }}
              axisLine={{ stroke: colors.grid }}
              tickFormatter={(v: number) => valueFormatter(v)}
              {...(yAxisLabel
                ? { label: { value: yAxisLabel, angle: -90, position: 'insideLeft', fill: colors.axisLabel, fontSize: 12, style: { textAnchor: 'middle' } } }
                : {})}
            />

            {/* Zero baseline so positive/negative columns read against it. */}
            <ReferenceLine y={0} stroke={colors.axis} />

            {/* Columns — colored by sign via per-bar cells. */}
            <Bar dataKey="value" isAnimationActive={false} radius={[2, 2, 0, 0]}>
              {rows.map((row) => (
                <Cell
                  key={row.month}
                  fill={row.value >= 0 ? colors.positive : colors.negative}
                />
              ))}
            </Bar>

            {/* Optional cumulative overlay line (same y-axis). */}
            {showCumulative ? (
              <Line
                dataKey="cumulative"
                stroke={colors.cumulative}
                strokeWidth={2}
                dot={false}
                isAnimationActive={false}
                connectNulls
              />
            ) : null}

            {/* Optional reference marker (e.g. break-even month). */}
            {hasReference ? (
              <ReferenceLine x={referenceMonth} stroke={colors.reference} strokeDasharray="4 3" />
            ) : null}

            <Tooltip
              cursor={{ fill: colors.exposureBar }}
              isAnimationActive={false}
              content={
                <ColumnTooltip
                  t={t}
                  valueLabel={valueLabel}
                  cumulativeLabel={showCumulative ? cumulativeLabel : undefined}
                  valueFormatter={valueFormatter}
                  colors={colors}
                />
              }
            />
          </ComposedChart>
        </ResponsiveContainer>
      </ChartFrame>
    </div>
  );
}

interface TooltipEntry {
  readonly payload?: ColumnRow;
}
interface ColumnTooltipProps {
  readonly active?: boolean;
  readonly payload?: readonly TooltipEntry[];
  readonly t: TFunction;
  readonly valueLabel: string;
  readonly cumulativeLabel?: string | undefined;
  readonly valueFormatter: (value: number) => string;
  readonly colors: ReturnType<typeof useChartColors>;
}

function ColumnTooltip({
  active,
  payload,
  t,
  valueLabel,
  cumulativeLabel,
  valueFormatter,
  colors,
}: ColumnTooltipProps): ReactNode {
  if (!active || !payload || payload.length === 0) return null;
  const row = payload[0]?.payload;
  if (!row) return null;
  return (
    <div className={styles.tooltip}>
      <span className={styles.tooltipLabel}>{t('charts.month', { month: row.month })}</span>
      <div className={styles.tooltipRow}>
        <span className={styles.tooltipKey}>
          <TooltipSwatch color={row.value >= 0 ? colors.positive : colors.negative} />
          {valueLabel}
        </span>
        <span className={styles.tooltipValue}>{valueFormatter(row.value)}</span>
      </div>
      {row.cumulative !== null && cumulativeLabel ? (
        <div className={styles.tooltipRow}>
          <span className={styles.tooltipKey}>
            <TooltipSwatch color={colors.cumulative} />
            {cumulativeLabel}
          </span>
          <span className={styles.tooltipValue}>{valueFormatter(row.cumulative)}</span>
        </div>
      ) : null}
    </div>
  );
}
